import { Check } from "lucide-react";
import { NavLink } from "react-router";

import { cn } from "@/lib/cn";
import { WIZARD_STEPS } from "@/features/profile/wizard/steps";
import type { Completeness } from "@/features/profile/types";

export interface StepperProps {
  currentKey: string;
  completeness?: Completeness;
  className?: string;
}

/**
 * Progress through the eleven steps.
 *
 * Every step is reachable at any time: someone who cannot find their passport
 * should be able to skip ahead and come back, not be trapped in a linear flow.
 */
export function Stepper({ currentKey, completeness, className }: StepperProps) {
  const statusByKey = new Map(
    (completeness?.steps ?? []).map((step) => [step.key, step]),
  );

  return (
    <nav aria-label="Profile steps" className={className}>
      <ol className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:gap-0.5 lg:overflow-visible lg:pb-0">
        {WIZARD_STEPS.map((step, index) => {
          const status = statusByKey.get(step.key);
          // An optional step is not in the report; nothing on it is owed.
          const done = status ? status.complete : Boolean(step.optional);

          return (
            <li key={step.key} className="shrink-0 lg:shrink">
              <NavLink
                to={`/onboarding/${step.key}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-sm px-3 py-2 text-base transition-colors",
                    "max-lg:whitespace-nowrap",
                    isActive || step.key === currentKey
                      ? "bg-surface font-semibold text-fg"
                      : "text-fg-muted hover:bg-ink-100 hover:text-fg",
                  )
                }
              >
                <span
                  className={cn(
                    "grid size-5 shrink-0 place-items-center rounded-full text-2xs font-bold",
                    done
                      ? "bg-accent text-white"
                      : step.key === currentKey
                        ? "bg-primary text-white ring-2 ring-white"
                        : "bg-ink-200 text-fg-muted",
                  )}
                  aria-hidden="true"
                >
                  {done ? <Check className="size-3" strokeWidth={3} /> : index + 1}
                </span>
                {/* The API names the step as this person sees it. */}
                <span className="truncate">{status?.title ?? step.title}</span>
              </NavLink>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
